import React, { useEffect, useState } from 'react';
import { Range, getTrackBackground } from 'react-range';
import Select, { MultiValue } from 'react-select';
import { toast } from '../../../../../ui/toast';
import { Button, Drawer } from '../../../../../ui';
import { MultiSelectListItem } from '../../../../../ui/data_source_filter/MultiSelectListItem';
import { ToggleInput } from '../../../../../ui/input/toggle_input/toggle_input';
import { saveFilter } from '../../../../../utils/api_service/JobOpenings';
import { JobApplicantCSV, JobApplicantSearchRequest, Skill } from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { TopicInput } from '../../../../questions/upload_file/topic_input';
import SkillWeighting from '../UI/SkillWeighting';

type AnalyticFilterProps = {
    isOpen: boolean,
    onCloseDrawer: () => void,
    setStatus: (status: string[]) => void | null,
    statusValue: string[],
    minMaxAge: number[],
    setMinMaxAge: (minMax: number[]) => void,
    behavioralAttributes: string,
    setBehavioralAttributes: React.Dispatch<React.SetStateAction<string>>,
    defaultJobTitles: string,
    setDefaultJobTitles: React.Dispatch<React.SetStateAction<string>>,
    setSimilarKeyword: React.Dispatch<React.SetStateAction<string>>,
    similarKeyword: string,
    loading: boolean,
    genderList: string[],
    setGenderList: (
        value: ((prevState: string[]) => string[]) | string[]
    ) => void,
    statusList: string[],
    setStatusList: (
        value: ((prevState: string[]) => string[]) | string[]
    ) => void,
    similarToggle: boolean,
    setSimilarToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    ageToggle: boolean,
    setAgeToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    searchObject: JobApplicantSearchRequest,
    setSearchObject: (
        jobApplicantSearchRequest: JobApplicantSearchRequest
    ) => void,
    searchCSVObject: JobApplicantCSV,
    setSearchCSVObject: (JobApplicantCSV: JobApplicantCSV) => void,
    keywordList: string[],
    setKeywordList: (
        value: ((prevState: string[]) => string[]) | string[]
    ) => void,
    jobOpId: string,
    extractedSkill: Skill[],
    setExtractedSkill: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    extractedJobTitles: Skill[],
    setExtractedJobTitles: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    extractedKeywords: Skill[],
    setExtractedKeywords: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    extractedBehavioralScore: Skill[],
    setExtractedBehavioralScore: (value: (((prevState: Skill[]) => Skill[]) | Skill[])) => void,
    weightingEnabled: boolean,
    setWeightingEnabled: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    keywordWeightage: boolean,
    setKeywordWeightage: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    similarBehavior: boolean,
    setSimilarBehavior: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    keywordToggle: boolean,
    setKeywordToggle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    similarJobTitle: boolean,
    setSimilarJobTitle: (value: (((prevState: boolean) => boolean) | boolean)) => void,
    setUseSkillPercentage: (value: (((prevState: boolean) => boolean) | boolean)) => void,
};

export const AnalyticFilter = ({
  isOpen,
  onCloseDrawer,
  setStatus,
  statusValue,
  minMaxAge,
  setMinMaxAge,
  defaultJobTitles,
  setDefaultJobTitles,
  behavioralAttributes,
  setBehavioralAttributes,
  setSimilarKeyword,
  similarKeyword,
  loading,
  genderList,
  setGenderList,
  statusList,
  setStatusList,
  similarToggle,
  setSimilarToggle,
  ageToggle,
  setAgeToggle,
  searchObject,
  setSearchObject,
  searchCSVObject,
  setSearchCSVObject,
  keywordList,
  jobOpId,
  extractedSkill,
  setExtractedSkill,
  extractedJobTitles,
  setExtractedJobTitles,
  extractedKeywords,
  setExtractedKeywords,
  extractedBehavioralScore,
  setExtractedBehavioralScore,
  weightingEnabled,
  setWeightingEnabled,
  keywordWeightage,
  setKeywordWeightage,
  similarBehavior,
  setSimilarBehavior,
  keywordToggle,
  setKeywordToggle,
  similarJobTitle,
  setSimilarJobTitle,
  setUseSkillPercentage,
}: AnalyticFilterProps) => {

  const [genderInput, setGenderInput] = useState<SelectOptions[]>([]);
  const [statusInput, setStatusInput] = useState<SelectOptions[]>(
    statusList.map((status) =>
      status === 'Unsuccessful-Pending'
        ? { value: status, label: 'Unsuccessful' }
        : { value: status, label: status },
    ),
  );

  useEffect(() => {
    setGenderInput(
      genderList.map((gender) => ({ value: gender, label: gender })),
    );
  }, [genderList]);

  const convertStringToObjects = (
    input: string,
  ): { label: string; value: string }[] => {
    if (input === '') {
      return [];
    } else {
      const value = input.split(':').map((item) => ({
        label: item,
        value: item,
      }));
      return value;
    }
  };

  const genderItems = [
    {
      value: 'Male',
      label: 'Male',
    },
    {
      value: 'Female',
      label: 'Female',
    },
    {
      value: 'Other',
      label: 'Other',
    },
  ];

  const statusItems = [
    {
      value: 'New',
      label: 'New',
    },
    {
      value: 'Suitable',
      label: 'Suitable',
    },
    {
      value: 'Auto-Assessment',
      label: 'Auto Assessment',
    },
    {
      value: 'Outstanding',
      label: 'Outstanding',
    },
    {
      value: 'Shortlisted',
      label: 'ShortListed',
    },
    {
      value: 'Unsuccessful-Pending',
      label: 'Unsuccessful',
    },
  ];

  const handleMultiSelectChange = (
    selectedValues: MultiValue<SelectOptions>,
  ) => {
    const selected = selectedValues.map((option) => option.value);
    setGenderList(selected);
    setGenderInput(selectedValues as SelectOptions[]);
  };

  const handleApplicationStatus = (
    selectedValues: MultiValue<SelectOptions>,
  ) => {
    const selected = selectedValues.map((option) => option.value);
    setStatusList(selected);
    const selectedStatus = selectedValues.map((option) => option.label);
    setStatus(selectedStatus);
    setStatusInput(selectedValues as SelectOptions[]);
  };

  const handleApplyChange = async () => {

    const jobTitles = extractedJobTitles.map(item => item.skill);
    const behavioralScore = extractedBehavioralScore.map(item => item.skill);
    const keywords = extractedKeywords.map(({ skill, weight }) => ({
      keyword: skill,
      weight: weight,
    }));
    const cleanedSkills = extractedSkill.map(({ skill, weight }) => ({
      skill,
      weight,
    }));

    setUseSkillPercentage(weightingEnabled);

    const data = {
      ...searchObject,
      age: {
        ...searchObject.age,
        min_age: minMaxAge[0],
        max_age: minMaxAge[1],
        include_unknown: ageToggle,
      },
      additional_skills: {
        skill_weightage: weightingEnabled,
        similar: similarToggle,
        skills: cleanedSkills.length === 0 ? [] : cleanedSkills,
      },
      job_title: {
        similar: similarJobTitle,
        job_titles: jobTitles.length === 0 ? [] : jobTitles,
      },
      keywords: {
        keyword_weightage: keywordWeightage,
        similar: keywordToggle,
        keywords: keywords.length === 0 ? [] : keywords,
      },
      behavioral_attributes: {
        similar: similarBehavior,
        behavioral_attributes:
          behavioralScore.length === 0 ? [] : behavioralScore,
      },
      gender: genderList,
      applicant_status: statusList,
    };

    try {
      await saveFilter(jobOpId, data);
    } catch (error) {
      toast.error('Error Loading Job Applicants', {
        position: 'bottom-center',
      });
    }

    setSearchObject(data);

    setSearchCSVObject({
      ...searchCSVObject,
      age: {
        ...searchCSVObject.age,
        min_age: minMaxAge[0],
        max_age: minMaxAge[1],
        include_unknown: ageToggle,
      },
      additional_skills: {
        skill_weightage: weightingEnabled,
        similar: similarToggle,
        skills: cleanedSkills.length === 0 ? [] : cleanedSkills,
      },
      behavioral_attributes: {
        similar: similarBehavior,
        behavioral_attributes:
          behavioralScore.length === 0 ? [] : behavioralScore,
      },
      job_title: {
        similar: similarJobTitle,
        job_titles: jobTitles.length === 0 ? [] : jobTitles,
      },
      gender: genderList,
      applicant_status: statusList,
      keywords: {
        keyword_weightage: keywordWeightage,
        similar: keywordToggle,
        keywords: keywords.length === 0 ? [] : keywords,
      },
    });
  };

  return (
    <Drawer
        isOpen={isOpen}
        onClose={onCloseDrawer}
        footerContent={
          <div className='flex justify-end gap-2.5 w-full'>
            <Button label='Apply' onClick={() => handleApplyChange()} variant='primary'/>
            <Button label='Close' onClick={onCloseDrawer} variant='secondary'/>
          </div>
        }
        title='Apply Filters'
        width='w-full md:w-[740px] lg:w-[800px]'
    >
      <div className='space-y-4'>
        { /* Section: Applicant Attributes */ }
        <section className='bg-white rounded-xl border border-gray-200 shadow-xs p-4 sm:p-5'>
          <div className='flex items-center gap-2 mb-3.5'>
            <span className='flex items-center justify-center w-6 h-6 rounded-md bg-emerald-50 text-emerald-700 text-xs font-semibold'>
              📊
            </span>
            <div>
              <h2 className='text-xs font-bold uppercase tracking-wider text-gray-800'>Graph Axis Filters</h2>
              <p className='text-[11px] text-gray-400'>Configure 3D axes, importance weighting, and term matching</p>
            </div>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-3.5 items-stretch'>
            { /* Skill(s) */ }
            <div className={`h-full flex flex-col ${loading ? 'animate-pulse' : ''}`}>
              <SkillWeighting
                  title={'X: Skill(s)'}
                  attributeName={'Skill'}
                  isWeightRequired={true}
                  extractedSkill={extractedSkill}
                  setExtractedSkill={setExtractedSkill}
                  weightingEnabled={weightingEnabled}
                  setWeightingEnabled={setWeightingEnabled}
                  similarEnabled={similarToggle}
                  setSimilarEnabled={setSimilarToggle}
              />
            </div>
            { /* Experience Job Titles */ }
            <div className={`h-full flex flex-col ${loading ? 'animate-pulse' : ''}`}>
              <SkillWeighting
                  title={'Y: Experience Job Title(s)'}
                  attributeName={'job title'}
                  extractedSkill={extractedJobTitles}
                  setExtractedSkill={setExtractedJobTitles}
                  weightingEnabled={weightingEnabled}
                  setWeightingEnabled={setWeightingEnabled}
                  similarEnabled={similarJobTitle}
                  setSimilarEnabled={setSimilarJobTitle}
              />
            </div>
            { /* Separator */ }
            <div className='col-span-full flex items-center gap-2.5 my-1'>
              <div className='flex-grow h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent'></div>
              <span className='inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-50 px-3 py-1 rounded-full border border-gray-200/80 shadow-2xs'>
                <svg className='w-3 h-3 text-emerald-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5' />
                </svg>
                3D Graph Extended Axes
              </span>
              <div className='flex-grow h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent'></div>
            </div>

            { /* Behavioral Attributes */ }
            <div className={`h-full flex flex-col ${loading ? 'animate-pulse' : ''}`}>
              <SkillWeighting
                  title={'Z: Behavioral Attribute(s)'}
                  attributeName={'attribute'}
                  extractedSkill={extractedBehavioralScore}
                  setExtractedSkill={setExtractedBehavioralScore}
                  weightingEnabled={weightingEnabled}
                  setWeightingEnabled={setWeightingEnabled}
                  similarEnabled={similarBehavior}
                  setSimilarEnabled={setSimilarBehavior}
              />
            </div>

            { /* Keywords Counter */ }
            <div className={`h-full flex flex-col ${loading ? 'animate-pulse' : ''}`}>
              <SkillWeighting
                  title={'Keyword Counter'}
                  attributeName={'keyword'}
                  isWeightRequired={true}
                  extractedSkill={extractedKeywords}
                  setExtractedSkill={setExtractedKeywords}
                  weightingEnabled={keywordWeightage}
                  setWeightingEnabled={setKeywordWeightage}
                  similarEnabled={keywordToggle}
                  setSimilarEnabled={setKeywordToggle}
              />
            </div>
          </div>
        </section>

        { /* Section: Filters */ }
        <section className='bg-white rounded-xl border border-gray-200 shadow-xs p-4 sm:p-5'>
          <div className='flex items-center gap-2 mb-3.5'>
            <span className='flex items-center justify-center w-6 h-6 rounded-md bg-blue-50 text-blue-700 text-xs font-semibold'>
              👤
            </span>
            <div>
              <h2 className='text-xs font-bold uppercase tracking-wider text-gray-800'>Candidate Attributes Filters</h2>
              <p className='text-[11px] text-gray-400'>Filter applicants by status, gender, and age demographics</p>
            </div>
          </div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-3.5'>
            { /* Applicant Status */ }
            <div className={loading ? 'animate-pulse' : ''}>
              <label className='block text-xs font-semibold text-gray-700 mb-1.5'>Applicant Status</label>
              { loading ? (
                <div className='h-9 w-full rounded-md bg-gray-200'/>
              ) : (
                statusValue.length === 0 && (
                  <Select
                      className='w-full text-xs'
                      placeholder='Select Application Status'
                      name='assessmentType'
                      options={statusItems}
                      onChange={handleApplicationStatus}
                      isClearable
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      components={{ Option: MultiSelectListItem }}
                      value={statusInput}
                      isMulti
                  />
                )
              ) }
            </div>

            { /* Gender */ }
            <div className={loading ? 'animate-pulse' : ''}>
              <label className='block text-xs font-semibold text-gray-700 mb-1.5'>Gender</label>
              { loading ? (
                <div className='h-9 w-full rounded-md bg-gray-200'/>
              ) : (
                <Select
                    className='w-full text-xs'
                    placeholder='Select Gender'
                    name='gender'
                    options={genderItems}
                    onChange={handleMultiSelectChange}
                    isClearable
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ Option: MultiSelectListItem }}
                    value={genderInput}
                    isMulti
                />
              ) }
            </div>
          </div>

          { /* Age Group */ }
          <div className='mt-4 pt-3.5 border-t border-gray-100'>
            <div className='flex items-center justify-between mb-2'>
              <label className='block text-xs font-semibold text-gray-700'>
                Age Group
              </label>
              <span className='text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200/70'>
                { minMaxAge[0] } – { minMaxAge[1] } yrs
              </span>
            </div>
            <div className='px-1 py-2'>
              <Range
                  values={minMaxAge}
                  step={1}
                  min={0}
                  max={100}
                  onChange={(newValues: number[]) => setMinMaxAge(newValues)}
                  renderTrack={({ props, children }) => (
                    <div
                        {...props}
                        className='h-1.5 w-full rounded-full'
                        style={{
                          ...props.style,
                          background: getTrackBackground({
                            values: minMaxAge,
                            colors: ['#E5E7EB', '#059669', '#E5E7EB'],
                            min: 0,
                            max: 100,
                          }),
                          alignSelf: 'center',
                        }}
                    >
                      { children }
                    </div>
                  )}
                  renderThumb={({ props, isDragged }) => (
                    <div
                        {...props}
                        style={{
                          ...props.style,
                          height: '18px',
                          width: '18px',
                          borderRadius: '50%',
                          backgroundColor: '#FFF',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                          border: isDragged ? '2px solid #059669' : '1px solid #D1D5DB',
                        }}
                    >
                      <div
                          className={`h-2 w-0.5 rounded-full ${isDragged ? 'bg-emerald-600' : 'bg-gray-400'}`}
                      />
                    </div>
                  )}
              />
            </div>
          </div>

          { /* Toggles */ }
          <div className='mt-3 pt-2.5 border-t border-gray-100'>
            <ToggleInput
                toggled={ageToggle}
                handleToggle={() => setAgeToggle(!ageToggle)}
                name='edit'
                informationText='Include Unknown Ages'
            />
          </div>
        </section>
      </div>
    </Drawer>
  );
};
